import type {
  ConditionGroup,
  ConvertTaskToRecurringPayload,
  CreateTaskPayload,
  CreateTaskRecurrencePayload,
  LexicalText,
  NextPageParams,
  PaginationMetadata,
  Task,
  TaskStatus,
  TaskVersionSnapshot,
  TaskVersionsResponse,
  UpdateTaskRecurrencePayload,
} from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export interface TasksResponse {
  tasks: Task[]
  users: Record<string, string>
  pagination?: PaginationMetadata
}

export function fetchTasks(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  search?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  if (search) {
    params.append("search", search)
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<TasksResponse>(context, `tasks?${params.toString()}`)
}

export function fetchHouseholdTaskCounts(context: ApiContext, householdId: string) {
  return apiContextFetch<{ todo_count: number }>(context, `households/${householdId}/tasks?counts_only=true`)
}

export function fetchHouseholdTasks(
  context: ApiContext,
  householdId: string,
  search?: string,
  projectId?: string,
  includeRecommended?: boolean,
  assignedTo?: string[],
  withoutProject?: boolean,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  if (search) {
    params.append("search", search)
  }

  if (projectId) {
    params.append("project_id", projectId)
  }

  if (includeRecommended) {
    params.append("include_recommended", "true")
  }

  assignedTo?.forEach((value) => params.append("assigned_to[]", value))

  if (withoutProject) {
    params.append("without_project", "true")
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<TasksResponse>(context, `households/${householdId}/tasks?${params.toString()}`)
}

export function fetchProjectTasks(context: ApiContext, projectId: string, search?: string) {
  const params = new URLSearchParams()
  params.append("project_id", projectId)

  if (search) {
    params.append("search", search)
  }

  return apiContextFetch<TasksResponse>(context, `tasks?${params.toString()}`)
}

export function fetchContactTasks(
  context: ApiContext,
  contactId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<TasksResponse>(context, `contacts/${contactId}/tasks?${params.toString()}`)
}

export function fetchBusinessTasks(
  context: ApiContext,
  businessId: string,
  search?: string,
  nextPageParams?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" }
) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  appendViewSortParams(params, viewSort)

  return apiContextFetch<TasksResponse>(context, `businesses/${businessId}/tasks?${params.toString()}`)
}

export function fetchTrustTasks(context: ApiContext, trustId: string, search?: string) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  return apiContextFetch<TasksResponse>(context, `trusts/${trustId}/tasks?${params.toString()}`)
}

export function fetchPersonTasks(context: ApiContext, personId: string, search?: string) {
  const params = new URLSearchParams()

  if (search) {
    params.append("search", search)
  }

  return apiContextFetch<TasksResponse>(context, `people/${personId}/tasks?${params.toString()}`)
}

export function fetchTask(context: ApiContext, id: string) {
  return apiContextFetch<Task>(context, `tasks/${id}`)
}

export function fetchTaskVersion(context: ApiContext, taskId: string, versionId: string) {
  return apiContextFetch<TaskVersionSnapshot>(context, `tasks/${taskId}/versions/${versionId}`)
}

export function restoreTaskVersion(context: ApiContext, taskId: string, versionId: string) {
  return apiContextFetch<Task>(context, `tasks/${taskId}/version_reverts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ version_id: versionId }),
  })
}

export function fetchTaskVersions(context: ApiContext, taskId: string, nextPageParams?: NextPageParams) {
  const params = new URLSearchParams()
  if (nextPageParams) {
    params.set("page", nextPageParams.page.toString())
    params.set("per_page", nextPageParams.per_page.toString())
  }

  const query = params.toString()
  return apiContextFetch<TaskVersionsResponse>(context, `tasks/${taskId}/versions${query ? `?${query}` : ""}`)
}

export async function createTask(context: ApiContext, taskData: CreateTaskPayload, overrideBookId?: string) {
  const {
    mentions,
    files,
    template_attachment_signed_ids,
    template_storage_attachment_ids,
    schwab_activity_id,
    ...taskParams
  } = taskData

  let fileSignedIds: string[] = []

  if (files && files.length > 0) {
    const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
    const { uploadFileDirectly } = await import("@/utils/direct-upload")

    fileSignedIds = await Promise.all(files.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  }

  return apiContextFetch<Task>(context, "tasks", {
    method: "POST",
    body: JSON.stringify({
      task: {
        ...taskParams,
        ...(fileSignedIds.length > 0 && { files: fileSignedIds }),
      },
      ...(schwab_activity_id ? { schwab_activity_id } : {}),
      ...(mentions ? { mentions } : {}),
      ...(template_attachment_signed_ids?.length ? { template_attachment_signed_ids } : {}),
      ...(template_storage_attachment_ids?.length ? { template_storage_attachment_ids } : {}),
      ...(overrideBookId ? { override_book_id: overrideBookId } : {}),
    }),
  })
}

export function createTaskRecurrence(context: ApiContext, data: CreateTaskRecurrencePayload, overrideBookId?: string) {
  const { schwab_activity_id, ...recurrenceParams } = data
  return apiContextFetch<Task>(context, "task_recurrences", {
    method: "POST",
    body: JSON.stringify({
      task_recurrence: recurrenceParams,
      ...(schwab_activity_id ? { schwab_activity_id } : {}),
      ...(overrideBookId ? { override_book_id: overrideBookId } : {}),
    }),
  })
}

export function convertTaskToRecurring(context: ApiContext, taskId: string, data: ConvertTaskToRecurringPayload) {
  return apiContextFetch<Task>(context, `tasks/${taskId}/recurrence`, {
    method: "POST",
    body: JSON.stringify({ recurrence: data }),
  })
}

export function deactivateTaskRecurrence(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `task_recurrences/${id}/deactivate`, {
    method: "PATCH",
  })
}

export function updateTaskRecurrence(context: ApiContext, id: string, data: UpdateTaskRecurrencePayload) {
  return apiContextFetch<Task>(context, `task_recurrences/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ task_recurrence: data }),
  })
}

export function updateTask(
  context: ApiContext,
  taskData: Partial<Task> & {
    id: string
    content?: LexicalText
    outcome_id?: string
    project_comment?: LexicalText
    form_responses?: Record<string, string | number | boolean | null>
  }
) {
  if (!taskData.id) {
    throw new Error("Task id is required for updating")
  }

  const { content, mentions, outcome_id, project_comment, form_responses, ...taskFields } = taskData

  return apiContextFetch<Task>(context, `tasks/${taskData.id}`, {
    method: "PUT",
    body: JSON.stringify({
      task: taskFields,
      ...(content && { comment: { content } }),
      ...(outcome_id && { outcome_id }),
      ...(project_comment && { project_comment: { content: project_comment } }),
      ...(form_responses && { form_responses }),
      ...(mentions ? { mentions } : {}),
    }),
  })
}

export function propagateRecurrence(context: ApiContext, taskId: string) {
  return apiContextFetch<Task>(context, `tasks/${taskId}/recurrence_propagation`, { method: "POST" })
}

export async function updateTaskWithFiles(
  context: ApiContext,
  taskId: string,
  newFiles: File[],
  existingSignedIds: string[] = []
) {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  const newSignedIds = await Promise.all(newFiles.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
  const allSignedIds = [...existingSignedIds, ...newSignedIds]

  return apiContextFetch<Task>(context, `tasks/${taskId}`, {
    method: "PUT",
    body: JSON.stringify({ task: { files: allSignedIds } }),
  })
}

export function deleteTask(context: ApiContext, id: string) {
  return apiContextFetch<Task>(context, `tasks/${id}`, {
    method: "DELETE",
  })
}

export function fetchTasksWithDateRange(context: ApiContext, startDate?: string, endDate?: string) {
  const params = new URLSearchParams()

  if (startDate) {
    params.append("start_date", startDate)
  }

  if (endDate) {
    params.append("end_date", endDate)
  }

  return apiContextFetch<TasksResponse>(context, `tasks?${params.toString()}`)
}

export interface GroupedTasksResponse {
  pagination: PaginationMetadata
  groups: {
    [groupKey: string]: { tasks: Task[]; total_count: number }
  }
}

export type TaskMeetingScope = "my_meetings" | "all_accessible"

export interface TaskQueueResponse {
  tasks: Task[]
  pagination: PaginationMetadata
  total_count?: number
}

export interface TaskApprovalCountsResponse {
  pending_count: number
  pending_or_rejected_count: number
}

export function fetchDashboardTasks(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  sort?: { field: string; direction: "asc" | "desc" },
  options: { perPage?: number; taskGroup?: "due_today"; timezone?: string } = {}
) {
  const { perPage = 10, taskGroup, timezone } = options
  const params = new URLSearchParams()

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  if (taskGroup) {
    params.append("task_group", taskGroup)
  }

  if (timezone) {
    params.append("timezone", timezone)
  }

  appendViewSortParams(params, sort ?? { field: "priority", direction: "desc" })
  params.append("paginate", "true")
  params.append("per_page", perPage.toString())

  return apiContextFetch<TasksResponse>(context, `tasks?${params.toString()}`)
}

export function fetchTaskQueue(
  context: ApiContext,
  statusScope: "pending" | "rejected" | "pending_or_rejected",
  nextPageParams?: NextPageParams,
  filterGroup?: ConditionGroup,
  meetingScope: TaskMeetingScope = "my_meetings"
) {
  const params = new URLSearchParams()
  params.append("meeting_scope", meetingScope)
  params.append("status_scope", statusScope)
  params.append("paginate", "true")

  if (filterGroup) {
    appendConditionGroupParams(params, filterGroup)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  return apiContextFetch<TaskQueueResponse>(context, `tasks?${params.toString()}`)
}

export function fetchTaskApprovalCounts(context: ApiContext) {
  return apiContextFetch<TaskApprovalCountsResponse>(context, "tasks/approval_counts")
}

export function fetchGroupedTasks(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  search?: string,
  nextPageParams?: NextPageParams,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  timezone?: string
) {
  const params = new URLSearchParams()

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  if (search) {
    params.append("search", search)
  }

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  appendViewSortParams(params, viewSort)

  if (timezone) {
    params.append("timezone", timezone)
  }

  params.append("grouped", "true")

  return apiContextFetch<GroupedTasksResponse>(context, `tasks?${params.toString()}`)
}

export function bulkUpdateTasks(
  context: ApiContext,
  {
    task_ids,
    status,
    assigned_to_id,
    assigned_team_id,
    deadline_at,
    priority,
    tag_ids,
  }: {
    task_ids: string[]
    status?: TaskStatus
    assigned_to_id?: string | null
    assigned_team_id?: string | null
    deadline_at?: string | null
    priority?: string | null
    tag_ids?: string[]
  }
) {
  return apiContextFetch<{ updated_ids: string[] }>(context, "tasks/bulk_update", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      task_ids,
      ...(status !== undefined && { status }),
      ...(assigned_to_id !== undefined && { assigned_to_id }),
      ...(assigned_team_id !== undefined && { assigned_team_id }),
      ...(deadline_at !== undefined && { deadline_at }),
      ...(priority !== undefined && { priority }),
      ...(tag_ids !== undefined && { tag_ids }),
    }),
  })
}

export function bulkDeleteTasks(context: ApiContext, { task_ids }: { task_ids: string[] }) {
  return apiContextFetch<{ deleted_ids: string[] }>(context, "tasks/bulk_destroy", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ task_ids }),
  })
}

export interface AllTaskIdsResponse {
  task_ids: string[]
  total_count: number
}

export function fetchAllTaskIds(context: ApiContext, viewFilterGroup?: ConditionGroup, search?: string) {
  const params = new URLSearchParams()

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  if (search) {
    params.append("search", search)
  }

  return apiContextFetch<AllTaskIdsResponse>(context, `tasks/all_ids?${params.toString()}`, {
    method: "GET",
  })
}
